import React, { useEffect, useLayoutEffect, useRef, useState } from 'react';
import classNames from 'classnames';
import {
  useStudioFullscreen,
  type TStudioFullscreenTargetId,
} from '../StudioFullscreen/context';
import './index.scss';

interface IFullScreenBtnProps {
  className?: string;
  style?: React.CSSProperties;
  children?: React.ReactNode;
  showButton?: boolean;
  targetId?: TStudioFullscreenTargetId;
  active?: boolean;
  priority?: number;
  /** @deprecated 按钮已统一改为悬浮展示，仅保留旧调用兼容。 */
  placement?: 'above' | 'inside';
  /** iframe 页面只在捕获真实 iframe 的 load 事件后开放全屏入口。 */
  attachToIframe?: boolean;
  /** @deprecated 原生全屏现在统一由 StudioFullscreenProvider 管理。 */
  nativeFullscreen?: boolean;
}

/** 注册当前 Studio 内容为全屏目标，悬浮按钮由 Studio Layout 统一渲染。 */
export default function FullScreenBtn(props: IFullScreenBtnProps) {
  const {
    className,
    style,
    children,
    showButton = true,
    targetId,
    active = true,
    priority = 100,
    placement = 'above',
    attachToIframe = false,
    nativeFullscreen,
    ...resetProps
  } = props;
  const fullscreen = useStudioFullscreen();
  const registerTarget = fullscreen?.registerTarget;
  const unregisterTarget = fullscreen?.unregisterTarget;
  const [iframeLoaded, setIframeLoaded] = useState(false);
  const wrapperRef = useRef<HTMLDivElement>(null);
  const generatedTargetIdRef = useRef(Symbol('studio-fullscreen-target'));
  const resolvedTargetId = targetId ?? generatedTargetIdRef.current;

  // 接口兼容字段由统一 Provider 接管，避免透传给业务子组件。
  void nativeFullscreen;

  useLayoutEffect(() => {
    if (!attachToIframe) return;

    let activeIframe: HTMLIFrameElement | null = null;
    const handleIframeLoad = () => setIframeLoaded(true);
    const isIframeSrcAlreadyLoaded = (iframe: HTMLIFrameElement): boolean => {
      if (!iframe.getAttribute('src')) return false;
      try {
        const iframeDocument = iframe.contentDocument;
        const documentUrl = iframeDocument?.URL || '';
        return iframeDocument?.readyState === 'complete'
          && Boolean(documentUrl)
          && documentUrl !== 'about:blank';
      } catch (_) {
        return false;
      }
    };
    const clearActiveIframe = () => {
      activeIframe?.removeEventListener('load', handleIframeLoad);
      activeIframe = null;
    };

    const syncIframe = () => {
      const iframe = wrapperRef.current?.querySelector<HTMLIFrameElement>('iframe') ?? null;
      if (iframe === activeIframe) return;

      clearActiveIframe();
      setIframeLoaded(false);
      if (!iframe) return;

      activeIframe = iframe;
      activeIframe.addEventListener('load', handleIframeLoad);
      if (isIframeSrcAlreadyLoaded(activeIframe)) {
        setIframeLoaded(true);
      }
    };

    syncIframe();
    const observer = new MutationObserver(syncIframe);
    if (wrapperRef.current) {
      observer.observe(wrapperRef.current, { childList: true, subtree: true });
    }

    return () => {
      observer.disconnect();
      clearActiveIframe();
    };
  }, [attachToIframe]);

  const canShowButton = showButton && (!attachToIframe || iframeLoaded);

  useEffect(() => {
    const element = wrapperRef.current;
    if (!registerTarget || !unregisterTarget || !element) return;

    registerTarget(resolvedTargetId, {
      element,
      ready: canShowButton,
      active,
      priority,
    });
    return () => unregisterTarget(resolvedTargetId);
  }, [
    active,
    canShowButton,
    priority,
    registerTarget,
    resolvedTargetId,
    unregisterTarget,
  ]);

  const content = children && React.Children.map(children, (child) => {
    if (!React.isValidElement(child) || typeof child.type === 'string') {
      return child;
    }
    return React.cloneElement(child, {
      isFullscreen: fullscreen?.isFullscreen ?? false,
      ...resetProps,
    });
  });

  return (
    <div
      ref={wrapperRef}
      className={classNames(
        'dbh-home-social-index-wrap',
        placement === 'inside' && 'dbh-home-social-index-wrap--inside',
        className,
      )}
      style={style}
    >
      {content}
    </div>
  );
}
